import { utilsService } from "./utils.service";
import { 
    IEffectsService, 
    DirectionsMap,
    IEffects,
    AnimationOptions,
    IEffectsElement
} from "../models/effects";

// Constants
const STRING = "string";
const BOOLEAN = "boolean";

/**
 * Effects Service - provides animation effects utilities
 
 */
class EffectsService implements IEffectsService {
    private _directions: DirectionsMap;
    private _effects: IEffects;

    constructor() {
        this._directions = this.createDirections();
        this._effects = this.createEffects();
    }

    /**
     * Create the directions map
     */
    private createDirections(): DirectionsMap {
        return {
            left: {
                reverse: "right",
                property: "left",
                transition: "translatex",
                vertical: false,
                modifier: -1
            },
            right: {
                reverse: "left",
                property: "left",
                transition: "translatex",
                vertical: false,
                modifier: 1
            },
            down: {
                reverse: "up",
                property: "top",
                transition: "translatey",
                vertical: true,
                modifier: 1
            },
            up: {
                reverse: "down",
                property: "top",
                transition: "translatey",
                vertical: true,
                modifier: -1
            },
            top: {
                reverse: "bottom"
            },
            bottom: {
                reverse: "top"
            },
            "in": {
                reverse: "out",
                modifier: -1
            },
            out: {
                reverse: "in",
                modifier: 1
            },
            vertical: {
                reverse: "vertical"
            },
            horizontal: {
                reverse: "horizontal"
            }
        };
    }

    /**
     * Create the effects object
     */
    private createEffects(): IEffects {
        const effects: IEffects = {
            enabled: true,
            Element: function(this: IEffectsElement, element: HTMLElement | JQuery) {
                this.element = $(element);
            } as any,
            promise: function(element: JQuery, options: AnimationOptions) {
                if (!element.is(":visible")) {
                    element.css({ display: element.data("olddisplay") || "block" }).css("display");
                }

                if (options.hide) {
                    element.data("olddisplay", element.data("olddisplay") || element.css("display")).hide();
                }

                if (options.init) {
                    options.init();
                }

                if (options.completeCallback) {
                    options.completeCallback(element); // call the external complete callback with the element
                }

                element.dequeue();
            },
            promiseShim: null as any, // Will be set below
            disable: function() {
                this.enabled = false;
                this.promise = this.promiseShim;
            },
            enable: function() {
                this.enabled = true;
                this.promise = this.animatedPromise!;
            }
        };

        // Set promiseShim to be the same as promise initially
        effects.promiseShim = effects.promise;

        return effects;
    }

    /**
     * Get the directions map for animations
     */
    get directions(): DirectionsMap {
        return this._directions;
    }

    /**
     * Get the effects object
     */
    get effects(): IEffects {
        return this._effects;
    }

    /**
     * Prepare animation options with defaults
     */
    prepareAnimationOptions(
        options: string | AnimationOptions,
        duration?: number | boolean | (() => void),
        reverse?: boolean | (() => void),
        complete?: () => void
    ): AnimationOptions {
        if (typeof options === STRING) {
            // options is the list of effect names separated by space e.g. animate(element, "fadeIn slideDown")

            // only callback is provided e.g. animate(element, options, function() {});
            if (utilsService.isFunction(duration)) {
                complete = duration as () => void;
                duration = 400;
                reverse = false;
            }

            if (utilsService.isFunction(reverse)) {
                complete = reverse as () => void;
                reverse = false;
            }

            if (typeof duration === BOOLEAN) {
                reverse = duration as boolean;
                duration = 400;
            }

            options = {
                effects: options,
                duration: duration as number,
                reverse: reverse as boolean,
                complete: complete
            };
        }

        return $.extend({
            //default options
            effects: {},
            duration: 400, //jQuery default duration
            reverse: false,
            init: $.noop,
            teardown: $.noop,
            hide: false
        }, options, { completeCallback: (options as AnimationOptions).complete, complete: $.noop }); // Move external complete callback, so deferred.resolve can be always executed.
    }

    /**
     * Animate one or more elements
     */
    animate(
        element: JQuery,
        options: string | AnimationOptions,
        duration?: number | boolean | (() => void),
        reverse?: boolean | (() => void),
        complete?: () => void
    ): JQuery {
        const effects = this._effects;
        const preparedOptions = this.prepareAnimationOptions.bind(this);
        let idx = 0;
        const length = element.length;
        let instance: JQuery;

        for (; idx < length; idx++) {
            instance = $(element[idx]);
            instance.queue(function(this: HTMLElement) {
                effects.promise($(this), preparedOptions(options, duration, reverse, complete));
            });
        }

        return element;
    }

    /**
     * Toggle CSS classes on element
     */
    toggleClass(element: JQuery, classes: string, options?: any, add?: boolean): JQuery {
        if (classes) {
            const classArray = classes.split(" ");

            $.each(classArray, (idx: number, value: string) => {
                element.toggleClass(value, add);
            });
        }

        return element;
    }

    /**
     * Create an effects element wrapper
     */
    fx(element: HTMLElement | JQuery): IEffectsElement {
        return new this._effects.Element(element);
    }
}

export const effectsService = new EffectsService();